/**
 * @file OrderStageEditDialog.tsx
 * @description Создание / редактирование стадии pipeline'а заказов
 * (phase10.5 шаг 8). По образу `StageEditDialog` для CRM-стадий.
 *
 * Особенности vs CRM:
 *  - kinds — enum `OrderStageKind` (CREATED / AWAITING_PAYMENT / PAID / ...);
 *  - менять kind можно ТОЛЬКО когда на стадии 0 заказов
 *    (kind = автоматика, см. orders.service.ts);
 *  - isActive=false тоже требует 0 заказов.
 *
 * API-ошибки показываем inline через `<Alert>` (как в CRM).
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  ORDER_STAGE_KIND_LABEL,
  createOrderStage,
  updateOrderStage,
  type OrderStageConfigDto,
  type OrderStageKind,
} from "../../lib/api-orders";

interface Props {
  mode: "create" | "edit";
  pipelineId: string;
  stage?: OrderStageConfigDto;
  onClose: () => void;
  onSaved: () => void;
}

const KINDS: OrderStageKind[] = [
  "CREATED",
  // phase19.15 (F30): стадия «Согласование доставки» — вне автодвижения F29.
  "PENDING_DELIVERY_AGREEMENT",
  "AWAITING_PAYMENT",
  "PAID",
  "PACKAGING",
  "PACKED",
  "HANDED_TO_CARRIER",
  "READY_FOR_PICKUP",
  "IN_TRANSIT",
  "ARRIVED_AT_PICKUP",
  "DELIVERED",
  "COMPLETED",
  "CANCELLED",
  "RETURN",
];

const PALETTE = [
  "#5a6b36",
  "#9e8b5a",
  "#c0a890",
  "#fbecce",
  "#7d8c4f",
  "#9e9483",
  "#c95a4f",
  "#3d4b1f",
] as const;
const DEFAULT_COLOR = PALETTE[0];

/** Опции выбора типа — из реестра подписей видов стадий. */
const KIND_OPTIONS = KINDS.map((k) => ({ value: k, label: ORDER_STAGE_KIND_LABEL[k] }));

export function OrderStageEditDialog({
  mode,
  pipelineId,
  stage,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<OrderStageKind>("CREATED");
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [slaHours, setSlaHours] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // В edit-режиме kind можно менять ТОЛЬКО если на стадии 0 заказов.
  // Иначе — disabled (бизнес-правило защищает консистентность автоматики).
  const kindLocked = mode === "edit" && (stage?.ordersCount ?? 0) > 0;
  // Архивировать (isActive=false) тоже нельзя если есть заказы.
  const activeLocked = mode === "edit" && (stage?.ordersCount ?? 0) > 0;

  useEffect(() => {
    setError(null);
    if (mode === "edit" && stage) {
      setCode(stage.code);
      setName(stage.name);
      setKind(stage.kind);
      setColor(stage.color ?? DEFAULT_COLOR);
      setSlaHours(stage.slaHours ? String(stage.slaHours) : "");
      setIsActive(stage.isActive);
    } else {
      setCode("");
      setName("");
      setKind("CREATED");
      setColor(DEFAULT_COLOR);
      setSlaHours("");
      setIsActive(true);
    }
  }, [mode, stage]);

  async function onSubmit() {
    setError(null);
    if (!code.trim()) {
      setError("Код стадии обязателен (латиница, цифры, _).");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(code.trim())) {
      setError("Код: только латиница нижнего регистра, цифры и _.");
      return;
    }
    if (!name.trim()) {
      setError("Название обязательно.");
      return;
    }
    const slaNum = slaHours.trim() ? parseInt(slaHours, 10) : undefined;
    if (slaNum !== undefined && (isNaN(slaNum) || slaNum < 0)) {
      setError("SLA в часах — положительное число.");
      return;
    }

    setSaving(true);
    try {
      if (mode === "create") {
        await createOrderStage(pipelineId, {
          code: code.trim(),
          name: name.trim(),
          kind,
          color,
          slaHours: slaNum,
        });
      } else {
        await updateOrderStage(pipelineId, stage!.id, {
          code: code.trim(),
          name: name.trim(),
          // kind отправляем только если разрешено менять
          ...(kindLocked ? {} : { kind }),
          color,
          slaHours: slaNum,
          // isActive отправляем только если разрешено менять
          ...(activeLocked ? {} : { isActive }),
        });
      }
      notify.success(mode === "create" ? "Стадия создана" : "Стадия обновлена");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Новая стадия заказа"
              : "Редактирование стадии"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? <Alert severity="error">{error}</Alert> : null}

          {/* На телефоне — столбиком: список типов не помещается в половину 360. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="os-code">Код</Label>
              <Input
                id="os-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="custom_step"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="os-kind">Тип (автоматика)</Label>
              <SelectField
                id="os-kind"
                value={kind}
                disabled={kindLocked}
                onChange={(next) => setKind(next as OrderStageKind)}
                options={KIND_OPTIONS}
                ariaLabel="Тип стадии"
              />
              {kindLocked ? (
                <span className="type-caption text-on-surface-variant">
                  Менять тип нельзя — на стадии есть заказы.
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="os-name">Название</Label>
            <Input
              id="os-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Сборка на складе"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Цвет</Label>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setColor(p)}
                  className={`size-7 rounded-full border-2 ${
                    color === p ? "border-on-surface" : "border-transparent"
                  }`}
                  style={{ backgroundColor: p }}
                  aria-label={`Цвет ${p}`}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="os-sla">SLA (часов до эскалации)</Label>
            <Input
              id="os-sla"
              type="number"
              min={0}
              max={8760}
              value={slaHours}
              onChange={(e) => setSlaHours(e.target.value)}
              placeholder="24"
            />
            <span className="text-xs text-on-surface-variant">
              Пусто — без SLA. Типовые: оплата ≤72ч, сборка ≤24ч, отгрузка ≤24ч.
            </span>
          </div>

          {mode === "edit" ? (
            <label className="flex min-h-12 cursor-pointer items-center gap-2 text-sm text-on-surface-variant">
              {/* Общий Checkbox вместо нативного (UI-контракт). */}
              <Checkbox
                checked={isActive}
                disabled={activeLocked}
                onCheckedChange={(checked) => setIsActive(checked === true)}
              />
              Активна
              {activeLocked ? (
                <span className="type-caption text-on-surface-variant">
                  (нельзя архивировать — есть заказы)
                </span>
              ) : null}
            </label>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
